import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Box, Breadcrumbs, Link, type SxProps, type Theme, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { getAdminBreadcrumbs } from "./adminBreadcrumbs";
import { PageHeader } from "./PageHeader";

export interface PageShellProps {
  children: React.ReactNode;
  title: string;
  icon?: React.ReactNode;
  gradientTitle?: boolean;
  titleAccessory?: React.ReactNode;
  headerActions?: React.ReactNode;
  contextControls?: React.ReactNode;
  footer?: React.ReactNode;
  breadcrumbs?: "admin";
  bodySx?: SxProps<Theme>;
  className?: string;
}

/** Shared page frame for admin and customer insights/report pages. */
export const PageShell: React.FC<PageShellProps> = ({
  children,
  title,
  icon,
  gradientTitle,
  titleAccessory,
  headerActions,
  contextControls,
  footer,
  breadcrumbs,
  bodySx,
  className,
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const adminBreadcrumbs =
    breadcrumbs === "admin" ? getAdminBreadcrumbs(location.pathname, title, t) : null;

  return (
    <Box className={className}>
      <Box sx={{ px: { xs: 2, md: 2.5 }, minWidth: 0 }}>
        {adminBreadcrumbs && (
          <Box sx={{ pt: 0, pb: 0, mb: 0 }}>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              sx={{
                color: "text.primary",
                "& .MuiBreadcrumbs-ol": { flexWrap: { xs: "wrap", md: "nowrap" } },
                "& .MuiBreadcrumbs-li": {
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                },
              }}
            >
              {adminBreadcrumbs.map((breadcrumb, index) => {
                const content =
                  index === 0 ? (
                    <>
                      <AdminPanelSettingsIcon sx={{ fontSize: "1em" }} />
                      <Box component="span">{breadcrumb.label}</Box>
                    </>
                  ) : (
                    breadcrumb.label
                  );
                if (breadcrumb.to) {
                  return (
                    <Link
                      key={breadcrumb.to}
                      component={RouterLink}
                      to={breadcrumb.to}
                      variant="body2"
                      color="text.primary"
                      underline="hover"
                      sx={{ "&:hover": { color: "primary.main" } }}
                    >
                      {content}
                    </Link>
                  );
                }
                return (
                  <Typography
                    key={breadcrumb.label}
                    component="span"
                    variant="body2"
                    color="text.primary"
                    noWrap={false}
                    sx={{
                      display: index === 0 ? "inline-flex" : undefined,
                      alignItems: "center",
                      gap: index === 0 ? 0.5 : undefined,
                      maxWidth: "100%",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {content}
                  </Typography>
                );
              })}
            </Breadcrumbs>
          </Box>
        )}
        <PageHeader
          title={title}
          icon={icon}
          gradientTitle={gradientTitle}
          titleAccessory={titleAccessory}
          headerActions={headerActions}
        />
        {contextControls}
        <Box sx={bodySx}>{children}</Box>
        {footer}
      </Box>
    </Box>
  );
};
